<template>
	<div>
		<div class="c-global-checkout__wrapper">
			<div class="c-global-checkout__wrapper-holder">
				<form
					@submit="submitForm"
					novalidate
					class="c-global-checkout__left-block"
					ref="deliveryForm"
				>
					<DsText htmlElement="h1" v-if="headline" text-type="heading-4xl">
						{{ headline }}
					</DsText>

					<DsText v-if="introduction" html-element="div" text-type="body-lg">
						<p v-html="introduction" />
					</DsText>

					<div v-if="showInfobox" class="c-global-checkout__infobox">
						<DsIcon icon-name="information-circle" size="sm" class="c-global-checkout__infobox__icon" />
						<DsText html-element="div">
							<p>{{ formErrorInfoText }}</p>
						</DsText>
						<ul>
							<li v-for="field in invalidFields" :key="field">
								<a :href="scrolltarget + field">
									<DsText html-element="span">
										{{ getFieldLabelByFieldName(field) }}
									</DsText>
								</a>
							</li>
						</ul>
					</div>

					<div class="c-global-checkout__form">
						<AddressForm
							:form-fields="formFields"
							:form-data="formData.deliveryInformation.billingAddress"
							:errors="errors"
							:updateFieldError="updateFieldError"
							:form-state="formState"
							:address-type="'shippingAddress'"
							@update-form="onUpdateForm"
						/>

						<ConsentProfileField
							v-if="consentChoiceFieldData"
							v-model="formData.consentChoice"
							:vm="consentChoiceFieldData"
							:errors="errors"
							:show-errors="formState.submitted"
							@validate="($event) => updateFieldError($event.key, $event.value)"
						/>

						<!-- Terms and Conditions -->
						<UniversalConsentField
							v-if="termsAndConditionFieldData"
							v-model="formData.termsAndCondition.termsAndConditionAccepted"
							v-bind="termsAndConditionFieldData"
							@validate="($event) => updateFieldError($event.key, $event.value)"
							:show-errors="formState.submitted"
							:consent-text="termsAndConditionFieldData.text"
							:url-text="termsAndConditionFieldData.title"
						/>
						<!-- Sensitive Data Condition -->
						<UniversalConsentField
							v-if="sensitiveDataConditionData"
							v-model="formData.sensitiveDataCondition.accepted"
							v-bind="sensitiveDataConditionData"
							@validate="($event) => updateFieldError($event.key, $event.value)"
							:show-errors="formState.submitted"
							:consent-text="sensitiveDataConditionData.conditionText"
						/>
						<!-- Simple Consent -->
						<UniversalConsentField
							v-if="simpleConsentFieldData"
							v-model="formData.simpleConsentChoice.acceptTerms"
							v-bind="simpleConsentFieldData"
							:show-errors="formState.submitted"
							:error-message="simpleConsentFieldData.consentAgreementErrorMessage"
							@validate="($event) => updateFieldError($event.key, $event.value)"
						/>
					</div>
					<DsButtonGroup desktop-alignment="start">
						<DsButton
							type="submit"
							variant="primary"
							:label-loading="buttonContinueLoadingText"
							:state="disableButton ? 'loading' : 'default'"
							data-testid="submit-address"
						>
							{{ buttonContinueText }}
						</DsButton>
					</DsButtonGroup>
				</form>
				<div class="c-global-checkout__right-block">
					<delivery-cart :labels="labels"></delivery-cart>

					<CartTotalAndMessages :labels="labels" :submitModel="vm" :basket="basket" />
				</div>
			</div>
		</div>
	</div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import { DsButton, DsButtonGroup, DsIcon, DsText } from '@coloplast/design-system';
import { focusOnField, formatApiMessages } from 'multisite/services/checkoutHelperService.js';
import { ignoreOnIsNewAddress } from 'shared/settings/constants';
import { mapActions, mapGetters, mapMutations } from 'vuex';
import { sendCheckoutDeliveryForm } from 'shared/services/globalCheckoutServices';
import { track_add_shipping_info } from 'shared/services/tracking/basketTrackingService';
import { track_address_change } from 'shared/services/tracking/checkoutTrackingService';
import AddressForm from '../SharedComponents/addressForm.vue';
import CartTotalAndMessages from '../OrderSummaryStepPage/summaryBlocks/cartTotalAndMessages.vue';
import ConsentProfileField from 'multisite/components/formElements/consentProfileField.vue';
import deliveryCart from '../SharedComponents/deliveryCart.vue';
import store from 'shared/store';
import UniversalConsentField from 'multisite/components/formElements/universalConsentField.vue';
import * as API from 'shared/types/globalCheckoutService.types';
import { AxiosError } from 'axios';
import { ValidationErrorObject } from 'shared/composables/handleFormErrors';

interface SimpleConsentVm {
	fieldId: string;
	fieldName: string;
	clientSideInputType: string;
	name: string;
	title: string;
	consentAgreementErrorMessage: string;
	consentText: string;
	consentTextExtended: string;
	showConsent: boolean;
	readLessLabel: string;
	readMoreLabel: string;
}

interface SensitiveDataVm {
	fieldId: string;
	fieldName: string;
	name: string;
	conditionText: string;
	url: string;
	urlText: string;
	errorMessage: string;
}

interface TermsAndConditionsVm {
	fieldId: string;
	fieldName: string;
	name: string;
	title: string;
	text: string;
	url: string;
	errorMessage: string;
}

interface FormField {
	data: {
		fieldName?: string;
		leftFieldName?: string;
		rightFieldName?: string;
		label?: string;
		title?: string;
		leftLabel?: string;
		rightLabel?: string;
		[key: string]: unknown;
	};
}

interface LastEditedField {
	id: string;
	fieldName: string;
	errorMessage: string | null;
}

interface ApiErrorResponse {
	message?: string;
}

export default defineComponent({
	components: {
		deliveryCart,
		AddressForm,
		DsText,
		CartTotalAndMessages,
		ConsentProfileField,
		DsButton,
		DsIcon,
		DsButtonGroup,
		UniversalConsentField,
	},
	name: 'delivery-checkout',
	store,
	props: {
		vm: {
			type: Object,
			default: () => {},
			required: true,
		},
		formFields: {
			type: Array,
			required: true,
		},
		headline: {
			type: String,
			default: null,
		},
		introduction: {
			type: String,
			default: null,
		},
		formErrorInfoText: {
			String,
			default: null,
		},
		buttonContinueText: {
			type: String,
			default: '',
		},
		buttonContinueLoadingText: {
			type: String,
			default: '',
		},
		labels: {
			type: Object,
			default: null,
		},
		stepNumber: {
			type: Number,
		},
		errorPageLink: {
			type: String,
			default: null,
		},
	},
	data() {
		// Generate the formData with only the relevant fields.
		// Make sure this correctly reflects the data from the VM
		const formData = {
			deliveryInformation: {
				billingAddress: {
					...this.vm?.deliveryInformation?.billingAddress,
				},
			},
			termsAndCondition: {
				termsAndConditionAccepted: false,
			},
			sensitiveDataCondition: {
				accepted: false,
			},
			consentChoice: {
				acceptTerms: false,
				consentChoiceId: 0,
			},
			simpleConsentChoice: {
				acceptTerms: false,
				consentAgreementUrl: '',
				showConsent: false,
				consentType: '',
			},
		} as API.SendCheckoutDeliveryFormPayload;

		// Clone all the values we need in the form from the vm.
		// This goes through all values, adding primitives, and filtering keys on objects.
		(Object.keys(this.vm) as (keyof typeof formData)[]).forEach((key) => {
			if (formData[key]) return;

			(formData as unknown as Record<string, unknown>)[key] = (this.vm as Record<string, unknown>)[key];
		});

		const errors: Record<string, ValidationErrorObject> = {};

		return {
			checkoutUrl: '/basket/',
			loaded: false,
			mappedBasketProducts: [],
			order: null,
			lastEditedField: null as null | LastEditedField,
			errors,
			formState: {
				submitted: false,
				submitting: false,
			},
			formData,
			isNewAddress: false,
			disableButton: false,
		};
	},
	watch: {
		'formData.deliveryInformation.billingAddress.deliveryInstruction'(val) {
			if (!val) return;

			if (val.toLowerCase() !== 'other') {
				this.formData.deliveryInformation.billingAddress.deliveryInstruction1 = undefined;
			}
		},
		formattedAddress: {
			handler(address) {
				this.formData.deliveryInformation.billingAddress = {
					...this.formData.deliveryInformation.billingAddress,
					...address,
				};
			},
			deep: true,
		},
	},
	computed: {
		sensitiveDataConditionData() {
			return this.getFieldData('sensitiveDataCondition') as SensitiveDataVm;
		},
		consentChoiceFieldData() {
			return this.getFieldData('consentChoice');
		},
		termsAndConditionFieldData() {
			return this.getFieldData('termsAndCondition') as TermsAndConditionsVm;
		},
		simpleConsentFieldData() {
			return this.getFieldData('simpleConsentChoice') as SimpleConsentVm;
		},
		...mapGetters({
			visitorID: 'getVisitorID',
			basket: 'getBasket',
			getProductsFromAccount: 'getProductsData',
			currency: 'getCurrency',
			formattedAddress: 'getFormattedAddress',
		}),
		invalidFields() {
			const invalidFields: string[] = [];
			Object.keys(this.errors).forEach((key) => {
				if (!this.errors[key].isValid) invalidFields.push(key);
			});
			return invalidFields;
		},
		scrolltarget() {
			return '#scrolltarget-';
		},
		showInfobox() {
			return (
				this.formState.submitted &&
				this.formErrorInfoText &&
				this.invalidFields.length > 0 &&
				this.invalidFields.length < 4
			);
		},
	},
	methods: {
		...mapMutations(['setFormattedAddress']),
		...mapActions(['fetchBasket', 'getProducts']),
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		getFieldData(fieldName: string): any {
			return (this.formFields as FormField[]).find((field) => field.data.fieldName === fieldName)?.data;
		},
		async submitForm(event: Event) {
			event.preventDefault();
			if (this.formState.submitting) return;
			if (this.disableButton) return;
			this.formState.submitted = true;
			const isValid = !this.invalidFields.length;

			if (isValid) {
				this.formState.submitting = true;
				this.disableButton = true;
				try {
					const { data } = await sendCheckoutDeliveryForm(this.formData);
					if (data?.redirectTo) {
						this.trackSubmit();
						window.location.assign(data.redirectTo);
					}
				} catch (error: unknown) {
					this.formState.submitting = false;
					this.handleSubmitErrors(error as AxiosError<ApiErrorResponse>);
					this.disableButton = false;
				}
			} else {
				this.formState.submitting = false;
				focusOnField(this.invalidFields[0]);
			}
		},
		redirectToErrorPage() {
			window.location.assign(this.errorPageLink);
		},
		handleSubmitErrors(error: AxiosError<ApiErrorResponse>) {
			const { response } = error;
			// no response
			if (!response) return this.redirectToErrorPage();

			const { data, status } = response;

			// 400 has form validation errors
			if (status === 400 && data?.message) {
				formatApiMessages(data.message).forEach(({ fieldName, message }) => {
					if (fieldName) {
						this.updateFieldError(fieldName, {
							...this.errors[fieldName],
							isValid: false,
							apiValidationError: message,
						});
					}
				});
			} else {
				// bad request and status is not 400, redirect to the error page
				this.redirectToErrorPage();
			}
		},
		trackSubmit() {
			track_add_shipping_info(this.basket);

			if (!this.isNewAddress) {
				track_address_change('edit', 'shippingAddress');
			}
		},
		fieldFocus(e: Event, name: string) {
			// for phone numbers, we need full property name instead of input field name
			const target = e?.target as HTMLElement;
			const fieldName = name || target.dataset.fieldName;

			if (fieldName) {
				// Extract information about the field from HTML attributes.
				this.lastEditedField = {
					id: target.id,
					fieldName: fieldName,
					errorMessage: target.getAttribute('aria-errormessage'),
				};
			}
		},
		updateFieldError(fieldName: keyof typeof this.errors, state: ValidationErrorObject) {
			this.errors[fieldName] = state;
		},
		getFieldLabelByFieldName(fieldName: string) {
			let name = fieldName;

			(this.formFields as FormField[]).forEach((field) => {
				const { data } = field;

				if (data.fieldName === fieldName) {
					// label for normal input fields, title for consent stuff
					name = data.label ?? data.title ?? name;
				}

				if (data.leftFieldName === fieldName) {
					name = data.leftLabel ?? name;
				}

				if (data.rightFieldName === fieldName) {
					name = data.rightLabel ?? name;
				}
			});

			return name;
		},
		onUpdateForm(e: { fieldName: string; value: unknown }) {
			(this.formData.deliveryInformation.billingAddress as Record<string, unknown>)[e.fieldName] = e.value;
		},
	},
	created() {
		this.getProducts();
	},
	mounted() {
		this.fetchBasket();
		this.loaded = true;

		const address = this.vm?.deliveryInformation?.billingAddress || {};
		this.setFormattedAddress({
			addressLine1: address.addressLine1,
			addressLine2: address.addressLine2,
			addressLine3: address.addressLine3,
			country: address.country,
			city: address.city,
			postalCode: address.postalCode,
			region: address.region,
		});
		if (this.invalidFields.some((f) => !ignoreOnIsNewAddress.includes(f))) {
			this.isNewAddress = true;
		}

		this.formData.simpleConsentChoice = this.vm.simpleConsentChoice;
	}
});
</script>
